import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { NotificationSettings } from '../components/NotificationComponents';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { 
  User, 
  ShoppingBag, 
  Clock,
  ChevronRight,
  Edit2,
  Save,
  Heart,
  Star,
  MapPin,
  Settings,
  RotateCcw,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl, RESTAURANT_PLACEHOLDER } from '../utils/images';
import { getApiErrorMessage } from '../utils/errorHelper';

const statusColors = {
  'Reçue': 'bg-orange-100 text-orange-700',
  'En préparation': 'bg-blue-100 text-blue-700',
  'Prête': 'bg-emerald-100 text-emerald-700',
  'Terminée': 'bg-gray-100 text-gray-700',
  'Annulée': 'bg-red-100 text-red-700',
};

export default function ClientProfile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { refreshCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [editing, setEditing] = useState(false);
  const [reorderingId, setReorderingId] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, order: null });
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch {
      console.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch {
      console.error('Error fetching favorites');
    } finally {
      setLoadingFavorites(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchFavorites();
  }, [fetchOrders, fetchFavorites]);

  const handleReorder = async (e, orderId, restaurantId) => {
    e.preventDefault();
    e.stopPropagation();
    
    setReorderingId(orderId);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/reorder`, {});
      
      toast.success(response.data.message);
      
      // Refresh cart context
      if (refreshCart) {
        await refreshCart();
      }
      
      // Navigate to restaurant page to complete the order
      navigate(`/restaurant/${restaurantId}`);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Erreur lors de la re-commande');
      toast.error(errorMessage);
    } finally {
      setReorderingId(null);
    }
  };

  const handleOpenReviewModal = (e, order) => {
    e.preventDefault();
    e.stopPropagation();
    setReviewData({ rating: 5, comment: '' });
    setReviewModal({ open: true, order });
  };

  const handleSubmitReview = async () => {
    if (!reviewModal.order) return;
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        restaurant_id: reviewModal.order.restaurant_id,
        order_id: reviewModal.order.id,
        rating: reviewData.rating,
        comment: reviewData.comment.trim()
      });
      
      toast.success('Merci pour votre avis !');
      setReviewModal({ open: false, order: null });
      
      // Marquer la commande comme ayant un avis (pour cacher le bouton)
      setOrders(prev => prev.map(o => 
        o.id === reviewModal.order.id ? { ...o, has_review: true } : o
      ));
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, "Erreur lors de lenvoi de l'avis");
      toast.error(errorMessage);
    } finally {
      setSubmittingReview(false);
    }
  };

  const removeFavorite = async (restaurantId) => {
    try {
      await axios.delete(`${API}/favorites/${restaurantId}`);
      setFavorites(favorites.filter(f => f.id !== restaurantId));
      toast.success('Retiré des favoris');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/auth/profile`, null, {
        params: { name: profileData.name, phone: profileData.phone }
      });
      toast.success('Profil mis à jour');
      setEditing(false);
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Mon compte</h1>

        <Tabs defaultValue="orders" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-gray-100 p-1 rounded-xl w-full grid grid-cols-3">
            <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-4">
              <ShoppingBag className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mes commandes</span>
              <span className="sm:hidden">Commandes</span>
            </TabsTrigger>
            <TabsTrigger value="favorites" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-4 relative">
              <Heart className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Favoris</span>
              <span className="sm:hidden">Favoris</span>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 sm:relative sm:top-0 sm:right-0 sm:ml-1 bg-red-500 text-white text-xs w-5 h-5 sm:w-auto sm:h-auto sm:px-1.5 sm:py-0.5 rounded-full flex items-center justify-center sm:inline">{favorites.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-4">
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Paramètres</span>
              <span className="sm:hidden">Réglages</span>
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-1/4 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune commande</h3>
                <p className="text-gray-500 mb-6">Vous n&apos;avez pas encore passé de commande</p>
                <Button 
                  onClick={() => navigate('/restaurants')}
                  className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                >
                  Parcourir les restaurants
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-shadow"
                    data-testid={`order-${order.id}`}
                  >
                    <Link to={`/commandes/${order.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-900">{order.order_number}</span>
                            <Badge className={statusColors[order.status] || 'bg-gray-100'}>
                              {order.status}
                            </Badge>
                          </div>
                          <p className="text-gray-600">{order.restaurant_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(order.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short'
                              })}
                            </span>
                            <span className="font-medium text-gray-900">
                              {order.total_amount.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                      </div>
                    </Link>
                    
                    {/* Re-order button for completed orders */}
                    {order.status === 'Terminée' && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                        <Button
                          onClick={(e) => handleReorder(e, order.id, order.restaurant_id)}
                          disabled={reorderingId === order.id}
                          variant="outline"
                          size="sm"
                          className="flex-1 rounded-full border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                        >
                          <RotateCcw className={`w-4 h-4 mr-2 ${reorderingId === order.id ? 'animate-spin' : ''}`} />
                          {reorderingId === order.id ? 'Ajout...' : 'Re-commander'}
                        </Button>
                        
                        {!order.has_review && (
                          <Button
                            onClick={(e) => handleOpenReviewModal(e, order)}
                            variant="outline"
                            size="sm"
                            className="flex-1 rounded-full border-amber-200 text-amber-600 hover:bg-amber-50"
                          >
                            <Star className="w-4 h-4 mr-2" />
                            Donner mon avis
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {loadingFavorites ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                    <div className="flex gap-4">
                      <div className="w-24 h-24 bg-gray-200 rounded-xl" />
                      <div className="flex-1">
                        <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : favorites.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun favori</h3>
                <p className="text-gray-500 mb-6">Ajoutez des restaurants à vos favoris pour les retrouver facilement</p>
                <Button 
                  onClick={() => navigate('/restaurants')}
                  className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                >
                  Découvrir les restaurants
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {favorites.map((restaurant) => (
                  <div key={restaurant.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                    <Link to={`/restaurant/${restaurant.id}`} className="flex gap-4 p-4">
                      <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0">
                        <img 
                          src={getFullImageUrl(restaurant.cover_image) || RESTAURANT_PLACEHOLDER}
                          alt={restaurant.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">{restaurant.name}</h3>
                        <p className="text-sm text-gray-500 mb-2 line-clamp-1">
                          {restaurant.cuisine_type?.join(', ') || 'Restaurant'}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {restaurant.avg_rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              {restaurant.avg_rating.toFixed(1)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {restaurant.city}
                          </span>
                        </div>
                      </div>
                    </Link>
                    <div className="px-4 pb-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => removeFavorite(restaurant.id)}
                        className="w-full text-red-500 border-red-200 hover:bg-red-50 rounded-full"
                      >
                        <Heart className="w-4 h-4 mr-2 fill-current" />
                        Retirer des favoris
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Personal Info Section */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
                  {!editing ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                      className="rounded-full"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleSaveProfile}
                      className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>Nom</Label>
                    {editing ? (
                      <Input
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        className="mt-1.5 rounded-xl"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{user?.name}</p>
                    )}
                  </div>

                  <div>
                    <Label>Email</Label>
                    <p className="mt-1 text-gray-900">{user?.email}</p>
                    <p className="text-xs text-gray-500 mt-1">L&apos;email ne peut pas être modifié</p>
                  </div>

                  <div>
                    <Label>Téléphone</Label>
                    {editing ? (
                      <Input
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="+33 6 12 34 56 78"
                        className="mt-1.5 rounded-xl"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{user?.phone || 'Non renseigné'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Notifications Section */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h2>
                <NotificationSettings />
                <p className="text-sm text-gray-500 mt-4">
                  Les notifications vous alertent quand votre commande change de statut (en préparation, prête, etc.)
                </p>
              </div>

              {/* Danger Zone */}
              <div className="bg-white rounded-2xl border border-red-100 p-6">
                <h2 className="text-lg font-semibold text-red-600 mb-4">Zone de danger</h2>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                >
                  Déconnexion
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => setReviewModal({ open, order: open ? reviewModal.order : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Donner mon avis</DialogTitle>
          </DialogHeader>
          
          {reviewModal.order && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="font-medium text-gray-900">{reviewModal.order.restaurant_name}</p>
                <p className="text-sm text-gray-500">Commande #{reviewModal.order.order_number}</p>
              </div>

              {/* Rating */}
              <div>
                <Label className="mb-2 block">Note</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewData({ ...reviewData, rating: star })}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= reviewData.rating
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {reviewData.rating === 1 && 'Très décevant'}
                  {reviewData.rating === 2 && 'Décevant'}
                  {reviewData.rating === 3 && 'Correct'}
                  {reviewData.rating === 4 && 'Très bien'}
                  {reviewData.rating === 5 && 'Excellent !'}
                </p>
              </div>

              {/* Comment */}
              <div>
                <Label className="mb-2 block">Commentaire (optionnel)</Label>
                <Textarea
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                  placeholder="Partagez votre expérience..."
                  className="rounded-xl"
                  rows={4}
                />
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmitReview}
                disabled={submittingReview}
                className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
              >
                {submittingReview ? 'Envoi en cours...' : 'Envoyer mon avis'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
